/* eslint-disable */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getBookAddresses, updateBookAddresses } from '@/store/books/actions';
import { getFile } from '@/store/files/actions';
import { createStudyPlan } from '@/store/study-plan/actions';
import { clearSubType } from '@/store/topics/actions';
import { createUserTopic, deleteUserTopic } from '@/store/user-topics/actions';

import { selectOpenDate } from '@/store/course/selectors';
import { selectLogin } from '@/store/login/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Spin, Tooltip } from 'antd';
import CalendarModal from '@/pages/CalendarModal/index.jsx';
import MaterialsEmptyPlaceholder from '@/pages/CollectionMaterials/MaterialsEmptyPlaceholder';
import AddressBook from '@/components/AddressBook/AddressBook';
import MaterialCard from '@/components/MaterialCard';
import TopicAddModal from '@/components/TopicAddModal';

import useIsMobile from '@shared/hooks/useIsMobile';
import {
  getWithoutSchedule,
  isCorpCourseWithOrWithoutApprove,
  isEvent,
  isExternalCourse,
} from '@shared/utils/topic-utils';

import Resources from '@/resources';
import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import css from './TopicCards.module.scss';

import { SCHEDULED } from '@/constants/study-plan-statuses';

const TopicCards = ({
  isLoading,
  isAddMaterialButton,
  isFavorite,
  topics,
  isFilterNotEmpty,
  searchText,
  isLibrary,
  xmas,
}) => {
  const { t } = useTranslation('collectionMaterials');
  const dispatch = useDispatch();

  const { currentUser, corpCourseBackground } = useSelector(selectUsers);
  const { externalLogin } = useSelector(selectLogin);
  const openDate = useSelector(selectOpenDate);

  const [calendarModal, setCalendarModal] = useState({
    visible: false,
    planningTopicId: null,
    planningTopicType: {},
    topicInfo: null,
    startDate: null,
  });
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [isFavs, setIsFavs] = useState({});
  const [addressState, setAdressState] = useState({
    isAddressBookVisible: false,
    calendarModalInfo: {},
    addresses: [],
  });
  const [addressId, setAddressId] = useState(0);
  const [isInPlan, setIsInPlan] = useState({});
  const [tablet] = useIsMobile(992);

  const handleFavoriteClick = topicId => {
    dispatch(
      createUserTopic(currentUser.id, topicId, () => {
        setIsFavs({ ...isFavs, [topicId]: true });
      })
    );
  };

  const handleDeleteClick = topicId => {
    dispatch(
      deleteUserTopic(topicId, true, true, () => {
        setIsFavs({ ...isFavs, [topicId]: false });
      })
    );
  };

  const handleStudyPlanClick = (
    topicId,
    planningMonths = [],
    comment,
    withoutSchedule,
    rest,
    planInfo,
    selectedEventId
  ) => {
    for (let i = 0; i < planningMonths.length; i++) {
      dispatch(
        createStudyPlan(
          currentUser.id,
          topicId,
          planningMonths[i],
          SCHEDULED,
          comment,
          () => {
            if (addressId > 0) {
              dispatch(updateBookAddresses(addressId, currentUser.id));
              setAddressId(0);
            }

            setIsInPlan({ ...isInPlan, [topicId]: true });
            closeCalendarModal();
          },
          false,
          getWithoutSchedule(calendarModal?.planningTopicType?.id, withoutSchedule),
          '',
          selectedEventId
        )
      );
    }
  };

  const handleAddressBookSubmit = currentAddressId => {
    const { topicId, type, startDate, topicInfo } = addressState?.calendarModalInfo;

    setAdressState({ isAddressBookVisible: false, calendarModalInfo: {}, addresses: [] });
    setAddressId(currentAddressId > 0 ? currentAddressId : 0);

    showCalendarModal(topicId, type, startDate, topicInfo);
  };

  const handleCancel = () => {
    setAddModalVisible(false);
    dispatch(clearSubType());
  };

  const handleTopicAddSuccess = () => {
    handleCancel();
  };

  const showCalendarModal = (topicId, type, startDate, topicInfo) => {
    setCalendarModal({
      visible: true,
      planningTopicId: topicId,
      planningTopicType: type,
      topicInfo,
      startDate,
    });
  };

  const closeCalendarModal = () => {
    setCalendarModal({
      visible: false,
      planningTopicId: null,
      planningTopicType: {},
      topicInfo: null,
      startDate: null,
    });
  };

  const showAddressBookModal = (topicId, type, startDate, topicInfo) => {
    dispatch(
      getBookAddresses(
        topicId,
        data => {
          const addresses = data.map(item => ({
            id: item.id,
            address: item.addressDto.address,
            accessible: item.accessible,
          }));

          if (addresses.length > 0) {
            setAdressState({
              addresses,
              isAddressBookVisible: true,
              calendarModalInfo: { topicId, type, startDate, topicInfo },
            });
            return;
          }

          showCalendarModal(topicId, type, startDate, topicInfo);
          setAdressState({ ...addressState, calendarModalInfo: {} });
        },
        () => setAdressState({ ...addressState, addresses: [] })
      )
    );
  };

  const disabledSelect = calendarModal?.planningTopicType?.id === 8;

  // Список декораций
  const decorationClassnames = ['snow', 'hat', 'garlands', 'cookiesAndSocks'];

  const getXmasDecoration = (key, chance = 0.3) => {
    // Генерируем псевдослучайное число из ключа
    const pseudoRandom = Math.abs(Math.sin(key) * 1000) % 1;

    // Если псевдослучайное число меньше шанса, возвращаем случайное украшение
    if (pseudoRandom < chance) {
      const randomIndex = Math.floor(Math.abs(Math.sin(key) * 10000) % decorationClassnames.length);
      return decorationClassnames[randomIndex];
    }

    // В противном случае, возвращаем none
    return 'none';
  };

  const AllCards = (
    <>
      {topics?.map((topic, key) => (
        <div
          data-qa='topicCard'
          key={`topic_${key}${topic.id}`}
          className={classNames(css['TopicCards-material-card'], {
            [css.favoriteCard]: isFavorite,
            [css.xmasDecoration]: xmas,
            [css[`xmasDecoration-${getXmasDecoration(topic.id)}`]]: xmas,
          })}
        >
          <MaterialCard
            dataQa='topic'
            topic={topic}
            isFav={isFavs && isFavs[topic.id]}
            isInPlan={isInPlan[topic.id]}
            userTopicId={topic.id}
            onDeleteClick={(...args) => dispatch(deleteUserTopic(args))}
            onDeleteClickCatalog={handleDeleteClick}
            onFavoriteClickCatalog={handleFavoriteClick}
            currentUser={currentUser}
            isExternalLogin={Boolean(externalLogin)}
            onFavoriteClick={(...args) => dispatch(createUserTopic(args))}
            onStudyPlanClick={topic.qiwiLibrary ? showAddressBookModal : showCalendarModal}
            getFile={(...args) => {
              dispatch(getFile(...args));
            }}
            domainCompany={currentUser.domainCompany}
            openDate={openDate?.openDate}
            corpCourseBackground={corpCourseBackground}
          />
        </div>
      ))}
    </>
  );

  const showAddMaterial =
    !tablet && isAddMaterialButton && !isLoading && currentUser?.domainCompany?.topicTypes?.length !== 0;

  return (
    <>
      <Spin
        spinning={isLoading}
        size={'large'}
        wrapperClassName={classNames(
          css['TopicCards-material-cards'],
          isLibrary && topics?.length === 0 && css['TopicCards-library'],
          isFavorite && css['TopicCards-material-cards-favorite'],
          isFavorite && topics?.length === 0 && css['TopicCards-material-cards-noCards']
        )}
      >
        <div className={css['TopicCards-alignmentBlock']}>
          {showAddMaterial && (
            <div className={css['TopicCards-material-card']}>
              <div
                className={classNames(css['TopicCards-add-material'], css['App-card-item'])}
                data-qa='userCollectionTopicsAddMaterialCard'
                onClick={() => setAddModalVisible(true)}
              >
                <Tooltip title={t('addToCollection')}>
                  <div className={css['TopicCards-add-material-icon']}>
                    <div className={css['TopicCards-add-material-icon-content']}>
                      <Resources.icons.Plus />
                    </div>
                  </div>
                </Tooltip>
                <h3>{t('addMaterial')}</h3>
              </div>
            </div>
          )}
          {topics?.length > 0
            ? AllCards
            : !isLoading && (
                <MaterialsEmptyPlaceholder
                  isExternalLogin={Boolean(externalLogin)}
                  isFavorite={isFavorite}
                  isSearching={isFilterNotEmpty == true}
                  searchName={searchText}
                  isLibrary={isLibrary}
                />
              )}
        </div>
      </Spin>

      {addModalVisible && (
        <TopicAddModal visible={addModalVisible} onTopicAddSuccess={handleTopicAddSuccess} onCancel={handleCancel} />
      )}
      {calendarModal.visible && (
        <CalendarModal
          topicInfo={calendarModal.topicInfo}
          comment={calendarModal?.topicInfo?.topicComment}
          eventTypeId={calendarModal?.planningTopicType?.id}
          disabledSelect={disabledSelect}
          dateEvent={dayjs(calendarModal.startDate).format('YYYY-MM-DD')}
          title={!disabledSelect ? t('selectPlan') : t('scheduleEvent')}
          open={calendarModal.visible}
          onOk={handleStudyPlanClick}
          onCancel={closeCalendarModal}
          dates={calendarModal?.topicInfo?.topicDates}
          okText={t('add')}
          {...(isExternalCourse(calendarModal?.planningTopicType?.id) && {
            okText: t('addToPlan'),
            headerFixToModal: true,
          })}
          {...(isEvent(calendarModal?.planningTopicType?.id) && {
            okText: t('addToPlan'),
            headerFixToModal: true,
          })}
          planningTopicId={calendarModal.planningTopicId}
          hasEvents={calendarModal?.topicInfo?.eventCount !== 0}
          selectMonthEvents={
            isCorpCourseWithOrWithoutApprove(calendarModal?.planningTopicType?.id) ||
            isExternalCourse(calendarModal?.planningTopicType?.id)
          }
          currentUser={currentUser}
        />
      )}

      {addressState.isAddressBookVisible && (
        <AddressBook
          textWindowTitle={t('addressBookWindowTitle')}
          textSubmitButton={t('addressBookSubmitButton')}
          textAccessible={t('addressBookaccessible')}
          textNotAccessible={t('addressBookNotAccessible')}
          textReadOnline={t('addressBookReadOnline')}
          onButtonClose={() => setAdressState({ isAddressBookVisible: false, calendarModalInfo: {}, addresses: [] })}
          onButtonSubmit={handleAddressBookSubmit}
          open={addressState.isAddressBookVisible}
          data={addressState?.addresses}
        />
      )}
    </>
  );
};

TopicCards.propTypes = {
  isLoading: PropTypes.bool,
  track: PropTypes.object,
  isLibrary: PropTypes.bool,
  xmas: PropTypes.bool, // отвечает за включение декораций на карточках
};

export default TopicCards;
